const qs = require('qs');
import { AppState } from '../helpers/app-state.js';
import { showFatalError } from '../helpers/error-ui.js';

// Safely read and parse JSON from sessionStorage, returning null for missing or invalid values
function safeGetJSON(ss, key) {
	const raw = ss.getItem(key);
	if (raw == null || raw === 'undefined') return null;
	try {
		return JSON.parse(raw);
	} catch (err) {
		console.warn('Failed to parse sessionStorage key "' + key + '":', err);
		return null;
	}
}

// Status line under the loading spinner; no-op once the page is showing
function setLoadingStatus(text) {
	// Inside .loader: #loading is a flex row during boot
	const host = document.querySelector('#loading .loader');
	if (!host) return;
	let el = document.getElementById('loadingStatus');
	if (!el) {
		el = document.createElement('p');
		el.id = 'loadingStatus';
		el.className = 'center-align text-secondary mt-4';
		host.appendChild(el);
	}
	el.textContent = text;
}

const SessionStorageService = {
	_callbacks: {},
	_refreshInProgress: false,

	init(callbacks) {
		this._callbacks = callbacks;
	},

	readSessionStorage() {
		let ss;
		if (AppState.typoError) return;
		try {
			if (!navigator.cookieEnabled) throw '';
			ss = window.sessionStorage;
		} catch (err) {
			console.error(err);
			document.getElementById('loading').innerHTML = `
				<div class="p-2 center-align">
					<div class="mb-4"><img src="./../../resources/assets/fix_car.svg" alt="Man fixing car" class="responsive-img px-12" style="width:500px; height:auto;"/></div>
					<h3 class="text-lg mt-4">Session storage blocked.</h3>
					<p class="text-secondary">Please set your browser to Allow all cookies, then try again. Thank you. (support@nthround.com)</p>
				</div>
			  `;
			throw 'Fatal error.';
		}

		userData = safeGetJSON(ss, 'user.Data');
		isAdmin = userData?.carlist?.includes('^' + client) || false;
		const tags = userData?.clients[client]?.tags || [];
		isCont = tags.includes('CONTRIBUTOR') || false;
		isSupv = tags.includes('SUPERVISOR') || false;
		isExec = tags.includes('EXECUTIVE') || false;
		VOA = tags.includes('VIEW ONLY ADMIN');

		clientData = safeGetJSON(ss, client + '.Data');
		shareholderData = safeGetJSON(ss, 'shareholder.Data');
		// Only read cap table if data exists in sessionStorage to avoid warnings
		if (NthUtil.sessionStore.hasCapTable(client)) {
			capTableData = NthUtil.sessionStore.readCapTable(client);
		} else {
			capTableData = [];
		}
		if (capTableData) {
			if (blurNames) {
				let blurNamesArray = blurNames.split('\r\n');
				for (let i = 0; i < capTableData.length; i++) capTableData[i].user_name = blurNamesArray[i];
				NthUtil.sessionStore.updateCapTable(client, capTableData);
			} else if (isSupv && !isExec) {
				// #6271: isExec always wins over isSupv scoping, matching generateCapTable below
				const scopeStatuses = (
					userData.clients[client].override_statuses?.length ? userData.clients[client].override_statuses : [userData.clients[client].status]
				).map((s) =>
					String(s || '')
						.toLowerCase()
						.trim()
				);

				capTableData = capTableData.filter((u) => scopeStatuses.includes(u.clients[client].status?.toLowerCase().trim()));
				NthUtil.sessionStore.updateCapTable(client, capTableData);
			}
		}
		this._callbacks.setLabels('read storage');
	},

	// Max 2 attempts total (1 retry) with a short delay in between. Both failure
	// modes below are commonly transient (a network blip, or a gateway/proxy
	// hiccup returning an HTML error page instead of JSON) — see
	// NTH-INVESTOR-PORTAL-D1 / NTH-INVESTOR-PORTAL-D4, GH #5994.
	_MAX_REFRESH_ATTEMPTS: 2,
	_capTablePrefetch: null,
	_stalePrefetch: null,
	_pickup: null,

	// A rebuild started on one page keeps running on the server after the browser leaves the page.
	// The marker lets the next page wait for that snapshot instead of starting a second rebuild.
	_REBUILD_PICKUP_MS: 60000,
	_rebuildKey() {
		return client + '.rebuildStartedAt';
	},
	_firstRefreshDone: false,
	_markRebuildStarted() {
		const marker = { t: Date.now(), id: Date.now().toString(36) + Math.random().toString(36).slice(2, 10) };
		try {
			sessionStorage.setItem(this._rebuildKey(), JSON.stringify(marker));
		} catch (e) {}
		return marker;
	},
	_clearRebuildMarker(marker) {
		try {
			if (safeGetJSON(sessionStorage, this._rebuildKey())?.id === marker.id) sessionStorage.removeItem(this._rebuildKey());
		} catch (e) {}
	},
	_inFlightRebuild() {
		try {
			const marker = safeGetJSON(sessionStorage, this._rebuildKey());
			return marker?.id && Date.now() - marker.t < this._REBUILD_PICKUP_MS ? marker : null;
		} catch (e) {
			return null;
		}
	},

	// Name, hash and finish time of the last saved snapshot (shareholderData latest=1); null if unavailable
	async _requestLatestMeta() {
		try {
			const res = await axios.get(shareholderURL, {
				params: {
					latest: 1,
					userid: authedUser,
					client: client,
					cookie: cookie,
					host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
				},
			});
			const name = res.data?.captable_data;
			if (typeof name !== 'string' || !name) return null;
			return {
				name,
				hash: res.data.captable_hash || null,
				takenAt: Number(name.slice(0, 10)) || null,
				checkedAt: Number(res.data.captable_checked_at) || null,
				rebuildId: res.data.captable_rebuild_id || null,
			};
		} catch (e) {
			console.warn('[refreshCapTable] latest snapshot info unavailable:', e?.message || e);
			return null;
		}
	},

	async _fetchSnapshot(name) {
		try {
			const response = await fetch(await window.getSnapshotUrl(name));
			if (!response.ok) return null;
			const snap = JSON.parse(await response.text());
			return Array.isArray(snap?.data) ? snap : null;
		} catch (e) {
			console.warn('[refreshCapTable] snapshot download failed:', e?.message || e);
			return null;
		}
	},

	// Wait for that exact rebuild (by id) to land; null if it doesn't in time or the backend can't tell
	async _waitForInFlight(marker) {
		const deadline = Math.max(marker.t + 45000, Date.now() + 5000);
		while (Date.now() < deadline) {
			const meta = await this._requestLatestMeta();
			if (meta?.rebuildId === marker.id) return meta;
			await new Promise((resolve) => setTimeout(resolve, 2000));
		}
		return null;
	},

	// Last saved snapshot (no rebuild); null if unavailable. Needs the shareholderData latest=1 backend mode.
	async _requestLatestSnapshot() {
		const meta = await this._requestLatestMeta();
		if (!meta) return null;
		const snap = await this._fetchSnapshot(meta.name);
		return snap ? { snap, hash: meta.hash, takenAt: meta.takenAt } : null;
	},

	_requestCapTable() {
		const marker = this._markRebuildStarted();
		const request = axios.get(shareholderURL, {
			params: {
				with_hash: 1,
				rebuild_id: marker.id,
				userid: authedUser,
				client: client,
				cookie: cookie,
				host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
			},
			paramsSerializer: function (params) {
				return qs.stringify(params, { arrayFormat: 'repeat' });
			},
		});
		// Finished (or failed) here: no one needs to pick it up. Cancelled by leaving the page: keep the marker.
		request.then(
			() => this._clearRebuildMarker(marker),
			(error) => {
				if (error?.response) this._clearRebuildMarker(marker);
			}
		);
		return request;
	},
	_REFRESH_RETRY_DELAY_MS: 1500,

	// Page load that already has this session's account data and is only missing the cap table
	// (e.g. Admin opened before Overview's cap table finished): get just the cap table, joining a
	// rebuild that is still running, instead of refetching the account data. False if data is missing.
	_capTableLoading: false, // a cap table load is under way on this page (set and cleared in refreshCapTable)
	loadCapTableOnly() {
		// Back from a preview (view only, so nothing changed): show the cap table saved before it
		const backFromPreview = sessionStorage.getItem(client + '.keepCapTable');
		sessionStorage.removeItem(client + '.keepCapTable');
		if (USE_PIN || location.search.includes('refresh=true')) return false;
		if (this._refreshInProgress || this._capTableLoading) return true;
		if (!sessionStorage.getItem('user.Data') || !sessionStorage.getItem(client + '.Data')) return false;
		this.readSessionStorage(); // the page may not have read storage yet (e.g. at DOMContentLoaded)
		if (!userData?.clients?.[client] || !clientData) return false;
		AppState.refreshTime = Date.now();
		const inFlight = this._inFlightRebuild();
		if (inFlight) this._pickup = { marker: inFlight, at: Date.now() };
		this._firstRefreshDone = true;
		const keep = backFromPreview && NthUtil.sessionStore.hasCapTable(client);
		console.log('Account data already loaded this session: ' + (keep ? 'reusing the saved cap table' : 'getting the cap table only'));
		this.refreshCapTable(true, { keepCapTable: !!keep });
		return true;
	},

	// opts.keepCapTable: refresh account data only and re-render this session's saved cap table
	// Page load with something missing: only the cap table if the account data is here, only the
	// account data if an admin page already has the cap table (e.g. back from a preview), else everything
	loadForPage() {
		if (this.loadCapTableOnly()) return;
		const adminPage = AppState.pn.startsWith('/admin') || AppState.pn.startsWith('/supervisor');
		const keepCapTable =
			adminPage &&
			!USE_PIN &&
			!isAlera &&
			!location.search.includes('refresh=true') &&
			NthUtil.sessionStore.hasCapTable(client) &&
			!sessionStorage.getItem(client + '.capTableFromSnapshot');
		if (keepCapTable) this.refreshSessionStorage(false, { keepCapTable: true });
		else this.refreshSessionStorage();
	},

	refreshSessionStorage(generate = true, opts = {}) {
		if (AppState.typoError) return;
		if (this._refreshInProgress) {
			console.log('refreshSessionStorage skipped - already in progress');
			return;
		}
		this.readSessionStorage();
		// don't get data if on signin, join, or forms
		if (AppState.pn.includes('/signin') || AppState.pn.includes('/join') || AppState.pn.includes('/forms')) return;
		AppState.refreshTime = Date.now();
		console.log('Starting refreshSessionStorage: ' + (Date.now() - AppState.refreshTime) + ' ms');

		let uid = authedUser;
		if (!uid) {
			console.log('No userid.  Signing out.');
			signOut();
			return;
		}

		// Native Cognito MFA challenge pending — no JWT exists yet, so this call would either
		// fail outright or be misread as "not logged in" and redirect to /signin/, abandoning
		// the challenge before the user has a chance to enter their code. checkAuth() (auth.js)
		// is already handling the challenge via this same nth.MfaPending signal; once it
		// completes, carryOnLoading() dispatches RefreshSessionStorage and this re-fires
		// naturally with a real cookie.
		if (!getCookie() && checkCookie('nth.MfaPending')) {
			console.log('refreshSessionStorage skipped - native MFA challenge pending');
			return;
		}

		this._refreshInProgress = true;
		this._fetchClientData(generate, uid, 1, opts);
	},

	// Split out of refreshSessionStorage() so a transient failure (bad network,
	// or the API returning something that isn't a JSON array) can retry the same
	// request instead of immediately giving up. `attempt` is 1-based.
	_fetchClientData(generate, uid, attempt, opts = {}) {
		const self = this;

		// Retries once (after _REFRESH_RETRY_DELAY_MS) if attempts remain, otherwise
		// gives up and shows the fatal-error UI exactly as before.
		const retryOrFail = (message, context, extra) => {
			if (attempt < self._MAX_REFRESH_ATTEMPTS) {
				console.warn('[refreshSessionStorage] attempt ' + attempt + ' failed (' + context + '), retrying in ' + self._REFRESH_RETRY_DELAY_MS + 'ms');
				setTimeout(() => self._fetchClientData(generate, uid, attempt + 1, opts), self._REFRESH_RETRY_DELAY_MS);
				return;
			}
			self._refreshInProgress = false;
			if (!AppState.typoError) showFatalError(message, context, extra);
		};

		// #6241: axios can still be loading (CDN blocked/slow) when this fires --
		// retry like any other transient failure instead of throwing ReferenceError.
		if (typeof axios === 'undefined') {
			// unpkg blocked or down: also load the same axios from jsDelivr, in time for the retry
			if (!self._axiosFallback) {
				self._axiosFallback = document.createElement('script');
				self._axiosFallback.src = 'https://cdn.jsdelivr.net/npm/axios@0.27.0/dist/axios.min.js';
				document.head.appendChild(self._axiosFallback);
			}
			retryOrFail(
				'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
				'axios not loaded (CDN blocked or slow?) before refreshSessionStorage fetch',
				{ page: window.location.pathname, attempt }
			);
			return;
		}

		cookie = getCookie();
		console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - AppState.refreshTime) + ' ms (attempt ' + attempt + ')');
		axios.defaults.withCredentials = cookie ? false : true;
		const aliasUserId = sessionStorage.getItem('alias.userid');
		const previewUser = sessionStorage.getItem('preview') === 'true' ? safeGetJSON(sessionStorage, 'shareholder.Data') : null;
		const previewUserId = previewUser?.clients?.[client] ? previewUser.userid : null;
		const viewid = previewUserId || aliasUserId || null;
		if (AppState.pn.startsWith('/app')) setLoadingStatus('Loading your client sites…');
		else setLoadingStatus(attempt > 1 ? 'Still loading your account data, retrying…' : 'Loading your account data…');

		// Admin pages: start the cap table build alongside mainir instead of after it (consumed in refreshCapTable)
		if (attempt === 1 && generate && !previewUserId && (AppState.pn.startsWith('/admin') || AppState.pn.startsWith('/supervisor'))) {
			// Only on the page's first load: a refresh after an edit must build fresh
			const inFlight = self._firstRefreshDone ? null : self._inFlightRebuild();
			if (inFlight) {
				self._pickup = { marker: inFlight, at: Date.now() };
			} else {
				const promise = self._requestCapTable();
				promise.catch(() => {});
				self._capTablePrefetch = { promise, at: Date.now() };
			}
			const usersPage = (AppState.pn.startsWith('/admin/users') && !AppState.pn.startsWith('/admin/users/edit')) || AppState.pn.startsWith('/supervisor');
			// Only when nothing is saved in this browser; otherwise the saved copy is already on screen
			if (usersPage && !NthUtil.sessionStore.hasCapTable(client)) self._stalePrefetch = { promise: self._requestLatestSnapshot(), at: Date.now() };
		}
		if (attempt === 1) self._firstRefreshDone = true;

		axios
			.get(clientURL, {
				params: {
					client: client,
					userid: authedUser,
					cookie: cookie,
					host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
					...(viewid ? { viewid } : {}),
				},
			})
			.then(function (response) {
				console.log('***Speed Test AFTER axios get : ' + (Date.now() - AppState.refreshTime) + ' ms');
				// Validate response is an array of objects (not a string error message/HTML page)
				if (!Array.isArray(response.data)) {
					retryOrFail(
						'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
						'API returned non-array response in refreshSessionStorage',
						{ responseType: typeof response.data, page: window.location.pathname, attempt }
					);
					return;
				}
				// If the user is not logged in build the public view
				if (!response.data[0]) {
					if (!AppState.pn.includes('/signin') && !AppState.pn.includes('/join') && !AppState.pn.includes('/forms'))
						window.location.replace('/signin/');
				} else if (!uid || !response.data[0]['user']) {
					console.log('refreshSessionStorage no user: ' + (Date.now() - AppState.refreshTime) + ' ms');
					clientData = response.data[0];
					self._callbacks.setLabels('no-user refresh');
					sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));
					document.dispatchEvent(new Event('StorageComplete'));
					if (uid.endsWith('@nthround.com')) {
						let userlistURL = addminURL + '?client=' + client + '&userid=' + authedUser + '&cookie=' + cookie + '&userlist=' + uid + '&admin=True';
						axios
							.post(userlistURL)
							.then(function (res) {
								console.log(res);
								location.reload();
							})
							.catch(function (err) {
								console.log(err);
							});
					}
				} else {
					if (!response.data[0]['user']) alert('Missing user data: responseData[0] = ', response.data[0]);

					userData = response.data[0]['user'];
					isAdmin = userData?.carlist?.includes('^' + client) || false;
					const tags = userData?.clients[client]?.tags || [];
					isCont = tags.includes('CONTRIBUTOR') || false;
					isSupv = tags.includes('SUPERVISOR') || false;
					isExec = tags.includes('EXECUTIVE') || false;
					VOA = tags.includes('VIEW ONLY ADMIN');

					let showTreemap = false;
					if (userData.clients[client] && userData.clients[client].hasOwnProperty('showTreemap')) showTreemap = userData.clients[client].showTreemap;
					sessionStorage.setItem('showTreemap', showTreemap);
					sessionStorage.removeItem('alias.Data');

					// CONSOLIDATED on 1-7-2022
					if (!response.data[1]['client']) alert('Missing client data responseData[1] = ', response.data[1]);
					clientData = response.data[1]['client'];
					self._callbacks.setLabels('refresh');

					// Detect spurious data
					if (EXTERNAL_DATA) {
						let foundTrans = false;
						AppState.classes.forEach((c) => {
							if (clientData.equity[c].transactions?.length) foundTrans = true;
						});
						if (foundTrans)
							reportAlert('portal.data', 'Unexpected Transactions (' + client + '.nthround.io)', JSON.stringify(clientData.equity).slice(0, 50));
					}

					// AUTOPOPULATE any userlisted user that is missing client data (get this with brand new account)
					if (!userData['clients'][client] && !userData.userid.toLowerCase().includes('nthround.com'))
						alert("Missing user client data userData['clients'] = ", userData['clients']);
					let userClient = userData['clients'][client];
					if (!userClient || !userClient['shares']) {
						userData = self._callbacks.autoPopulateUser(userData.userid, userData);
					}

					// The client chooser (/app) never shows transactions, and leaving it clears the session
					const onChooser = AppState.pn.startsWith('/app');
					if (STD_EXT_DATA && !onChooser) self._callbacks.getStandardTransactionsFromFile('userData', null, false, false, false, '');
					else if (isAlera && !onChooser) self._callbacks.getAleraHoldingsFromFile('userData', null, false, false, false, '');

					sessionStorage.setItem('user.Data', JSON.stringify(userData));
					sessionStorage.setItem(client + '.Data', JSON.stringify(clientData));

					console.log('Client/User complete: ' + (Date.now() - AppState.refreshTime) + ' ms');
					if (!AppState.pn.includes('/admin') && !AppState.pn.includes('/supervisor') && !EXTERNAL_DATA) {
						document.dispatchEvent(new Event('StorageComplete'));
						let goToAdmin = document.getElementById('goToAdmin');
						if (goToAdmin) {
							goToAdmin.style.pointerEvents = 'none';
							goToAdmin.style.backgroundColor = stdColor[1];
							goToAdmin.innerHTML = 'Go to Admin (loading...)';
						}
					}

					self.refreshCapTable(generate, opts);
				}
				self._refreshInProgress = false;
			})
			.catch(function (err) {
				retryOrFail(
					'Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)',
					'refreshSessionStorage network/API error',
					{ error: String(err), page: window.location.pathname, attempt }
				);
			});
	},

	// A cap table read from storage can be hours old (a long-lived tab, Preview after Preview). Once a page has drawn,
	// one older than this is checked against the server in the background, and redrawn if it changed.
	CAP_TABLE_MAX_AGE_MS: 10 * 60 * 1000,
	revalidateIfOld() {
		if (USE_PIN || isAlera || this._refreshInProgress || this._capTableLoading) return; // Alera rebuilds on every load
		if (!AppState.pn.startsWith('/admin') && !AppState.pn.startsWith('/supervisor')) return;
		if (!NthUtil.sessionStore.hasCapTable(client)) return;
		const at = Number(sessionStorage.getItem(client + '.CapTableAt')) || 0;
		if (Date.now() - at < this.CAP_TABLE_MAX_AGE_MS) return;
		console.log(
			'[refreshCapTable] saved cap table is ' + (at ? Math.round((Date.now() - at) / 60000) + ' min' : 'of unknown') + ' old: checking for changes'
		);
		this.refreshCapTable(true, { revalidate: true });
	},

	async refreshCapTable(generate = true, { keepCapTable = false, revalidate = false } = {}) {
		/* Retrieves cap table as indicated by the generate parameter
			param generate:
				If true, generates cap table traditionally
				If false, retrieves the latest cap table from s3
		*/
		const self = this;

		// MAGIC REFRESH (investor-agreement) for Nth Rounders
		const _isPageReload = (performance.getEntriesByType('navigation')[0] || {}).type === 'reload';
		if (_isPageReload && window.location.href.includes('/investor-agreement/') && authedUser.includes('@nthround.com')) {
			let userClientData = {
				investor: {
					complete: false,
					entity: false,
					income: false,
					netWorth: false,
					knowledge: false,
				},
				joinder: {
					complete: false,
				},
				operatingAgreement: {
					complete: false,
				},
			};
			axios
				.post(
					postURL,
					JSON.stringify({
						eventName: 'index.cap_table_refreshed',
						userid: authedUser,
						client: client,
						cookie: cookie,
						clients: {
							[client]: userClientData,
						},
					})
				)
				.then(function (res) {
					console.log(res);
				})
				.catch(function (err) {
					console.error(err);
				});

			userData.clients[client].investor = userClientData.investor;
			userData.clients[client].joinder = userClientData.joinder;
			userData.clients[client].operatingAgreement = userClientData.operatingAgreement;
		}

		const previewingShareholder = !!(sessionStorage.getItem('preview') === 'true' && safeGetJSON(sessionStorage, 'shareholder.Data')?.clients?.[client]);

		// The client chooser (/app) never shows a cap table, and leaving it clears the session anyway
		if (AppState.pn.startsWith('/app')) {
			document.dispatchEvent(new Event('CapTableReady'));
			return;
		}

		// if just a user, bail out
		if (!generate && !AppState.pn.startsWith('/admin') && !AppState.pn.startsWith('/supervisor')) {
			document.dispatchEvent(new Event('CapTableReady'));
			if (!isAdmin || previewingShareholder) return;
		}

		// Non-admin users should never receive the full cap table, but if generate=true
		// (e.g. after a proxy vote), fire-and-forget the API call so the backend creates
		// a fresh snapshot for admins — just discard the response.
		if ((!isAdmin || previewingShareholder) && !isSupv && !isExec && !AppState.pn.startsWith('/admin') && !AppState.pn.startsWith('/supervisor')) {
			if (generate && !previewingShareholder) {
				axios
					.get(shareholderURL, {
						params: {
							userid: authedUser,
							client: client,
							cookie: cookie,
							host: window.location.hostname === 'localhost' ? client + '.nthround.io' : window.location.host,
						},
						paramsSerializer: function (params) {
							return qs.stringify(params, { arrayFormat: 'repeat' });
						},
					})
					.catch(() => {});
			}
			document.dispatchEvent(new Event('CapTableReady'));
			return;
		}

		console.log('Starting Refresh CapTable: ' + (Date.now() - AppState.refreshTime) + ' ms');
		self._capTableLoading = true;

		// Get cap table data
		if (true) {
			console.log('Getting ShareholderData: ' + (Date.now() - AppState.refreshTime) + ' ms');
			let capTableName = clientData.captable_data;

			let freshApplied = false;
			let shownStale = null; // { hash, label } once the last snapshot is on screen
			let stalePromise = null;

			// No data yet: show the error page. Stale data on screen: just warn.
			const capTableFailed = (context, extra) => {
				self._capTableLoading = false;
				shownStale?.label?.dismiss();
				if (capTableData?.length) {
					console.error('[refreshCapTable] ' + context, extra || '');
					window.showToast?.('error-toast', 'Could not refresh the cap table. Showing the last loaded data.');
					return;
				}
				window.showLoading?.();
				showFatalError('Oops, this is awkward... Please try again in a few minutes! Thank you. (support@nthround.com)', context, extra);
			};

			// Saved copy as { data: [{ Item }] }, or null (Alera rebuilds, except back from a preview)
			const savedAsResponse = () => {
				const rows = (keepCapTable || !isAlera) && NthUtil.sessionStore.hasCapTable(client) ? NthUtil.sessionStore.readCapTable(client) : null;
				return rows?.length ? { data: rows.map((Item) => ({ Item })), saved: true } : null;
			};
			// Render a cap table and record its fingerprint; replaces the last snapshot if one is on screen
			// fromServer: built or confirmed by the server just now, so its age restarts
			const applyCapTable = (res, hash, note, fromServer = false) => {
				freshApplied = true;
				if (fromServer) sessionStorage.setItem(client + '.CapTableAt', String(Date.now()));
				sessionStorage.removeItem(client + '.capTableFromSnapshot');
				console.log('### ' + note + ': ' + (Date.now() - AppState.refreshTime) + ' ms');
				generateCapTable(res);
				self._capTableLoading = false;
				NthUtil.sessionStore.setCapTableHash(client, hash);
				if (shownStale) {
					shownStale.label?.dismiss();
					document.dispatchEvent(new CustomEvent('RenderCapTable', { detail: { skipListeners: true } }));
					window.M?.toast({ html: 'Updated with the latest changes', displayLength: 4000 });
				}
			};
			// The last snapshot already on screen has this fingerprint: nothing more to do
			const confirmShownStale = async (hash) => {
				if (!stalePromise) return false;
				await stalePromise;
				if (!shownStale?.hash || !hash || shownStale.hash !== hash) return false;
				freshApplied = true;
				self._capTableLoading = false;
				sessionStorage.setItem(client + '.CapTableAt', String(Date.now()));
				shownStale.label?.dismiss();
				if (!shownStale.quiet) window.M?.toast({ html: 'Confirmed up to date', displayLength: 2500 });
				return true;
			};

			// No rebuild when generate=false (e.g. use_authenticator off) or for an admin on a shareholder page:
			// this session's copy, else the last snapshot. A snapshot is flagged so admin pages still rebuild.
			const adminOnShareholderPage = isAdmin && !AppState.pn.startsWith('/admin') && !AppState.pn.startsWith('/supervisor');
			const noRebuild = (!generate || adminOnShareholderPage) && capTableName && !location.search.includes('refresh=true');
			const saved = !revalidate && (keepCapTable || noRebuild) ? savedAsResponse() : null;
			if (revalidate) {
				// The saved copy is already drawn: rebuild, and redraw only if the server's differs
				stalePromise = Promise.resolve();
				shownStale = { hash: NthUtil.sessionStore.getCapTableHash(client), quiet: true };
				await generateCapTableLegacy();
			} else if (saved) {
				applyCapTable(saved, NthUtil.sessionStore.getCapTableHash(client), 'Using the cap table saved this session');
			} else if (noRebuild) {
				setLoadingStatus('Loading the cap table…');
				const snapshot = await self._fetchSnapshot(capTableName);
				if (snapshot) {
					applyCapTable(snapshot, null, 'Got the saved snapshot ' + capTableName);
					if (adminOnShareholderPage) sessionStorage.setItem(client + '.capTableFromSnapshot', '1');
				} else await generateCapTableLegacy();
			} else {
				// Users page: draw the last saved snapshot while the fresh one builds
				const stale = self._stalePrefetch;
				self._stalePrefetch = null;
				if (stale && Date.now() - stale.at < 60000) {
					stalePromise = stale.promise;
					stale.promise.then((last) => {
						if (freshApplied || !last) return;
						console.log('### Showing last snapshot while rebuilding: ' + (Date.now() - AppState.refreshTime) + ' ms');
						generateCapTable(last.snap);
						NthUtil.sessionStore.setCapTableHash(client, last.hash);
						const asOf = last.takenAt ? moment.unix(last.takenAt).calendar() : 'the last save';
						shownStale = {
							hash: last.hash,
							label: window.M?.toast({ html: 'Showing data as of ' + asOf + ' \u00b7 checking for updates\u2026', displayLength: 120000 }),
						};
					});
				}
				// A rebuild started on the previous page is still running on the server: wait for it
				const pickup = self._pickup;
				self._pickup = null;
				if (pickup && Date.now() - pickup.at < 60000) pickUpInFlight(pickup.marker);
				else generateCapTableLegacy();
			}
			async function pickUpInFlight(marker) {
				console.log('### Waiting for the rebuild started ' + (Date.now() - marker.t) + ' ms ago on the previous page');
				setLoadingStatus('Finishing the cap table update already in progress…');
				const meta = await self._waitForInFlight(marker);
				if (!meta) {
					console.warn('[refreshCapTable] earlier rebuild did not land in time; rebuilding here');
					return generateCapTableLegacy();
				}
				if (await confirmShownStale(meta.hash)) return;
				const snap = await self._fetchSnapshot(meta.name);
				if (!snap) return generateCapTableLegacy();
				applyCapTable(snap, meta.hash, 'Picked up the earlier rebuild (' + meta.name + ')', true);
			}
			async function generateCapTableLegacy(attempt = 1) {
				/** Our legacy/old way of generating captables **/
				console.log('### GENERATING CAP TABLE IMAGE ###');
				const holders = clientData?.userlist?.length;
				setLoadingStatus(
					(attempt > 1 ? 'Still building the cap table, retrying' : 'Building the cap table') +
						(holders > 1 ? ' for ' + holders.toLocaleString() + ' holders' : '') +
						(holders > 300 ? '. Large accounts can take 20–30 seconds…' : '…')
				);
				console.log('***Speed Test - BEFORE axios.get() : ' + (Date.now() - AppState.refreshTime) + ' ms');
				let capTableRes = null;
				let freshHash = null;
				let failure = 'unknown';
				let reusedSaved = false;
				// Use the request started alongside mainir, if fresh; retries always make a new one
				const pre = self._capTablePrefetch;
				self._capTablePrefetch = null;
				const prefetched = attempt === 1 && pre && Date.now() - pre.at < 60000 ? pre.promise : null;
				try {
					const res = await (prefetched || self._requestCapTable());
					// with_hash=1 wraps the response as { url | data, hash }
					let data = res.data;
					if (data && typeof data === 'object' && !Array.isArray(data) && ('url' in data || 'data' in data)) {
						freshHash = data.hash || null;
						data = 'url' in data ? data.url : data.data;
					}
					// The server only sends the cap table to admins, supervisors and executives
					if (data === 'Snapshot updated') {
						console.warn('[refreshCapTable] no cap table for this user on ' + client);
						self._capTableLoading = false;
						document.dispatchEvent(new Event('CapTableReady'));
						return;
					}
					if (attempt === 1 && (await confirmShownStale(freshHash))) return;
					// Same fingerprint as the copy saved in this browser: reuse it instead of downloading again
					if (attempt === 1 && !stalePromise && freshHash && freshHash === NthUtil.sessionStore.getCapTableHash(client)) {
						capTableRes = savedAsResponse();
						reusedSaved = !!capTableRes;
					}
					if (capTableRes) {
						// reused the saved copy
					} else if (Array.isArray(data)) {
						/** handles image response **/
						console.log('***Speed Test - AFTER axios.get() : ' + (Date.now() - AppState.refreshTime) + ' ms');
						let contentLength = Number(res.headers['content-length']) / 1024;
						console.log('### Got NEW ShareholderData (' + contentLength.toFixed(0) + ' KB): ' + (Date.now() - AppState.refreshTime) + ' ms');
						if (contentLength > 5200) {
							let subj = 'Lambda Payload Size Alert (' + client + '.nthround.io)';
							let msg =
								'Message length = ' +
								contentLength.toFixed(0) +
								' KB.<br><br>Consider adding ' +
								client +
								' to LARGE_CLIENTS array in returnShareholderData lambda function and to shouldCompressClient() in auth.js.';
							reportAlert('portal.size', subj, msg);
							console.log(msg);
						}
						capTableRes = { data };
					} else if (typeof data === 'string' && data.startsWith('http')) {
						/** handles url response **/
						console.log('### Getting Cap Table Image from URL ###');
						setLoadingStatus('Downloading the cap table…');
						let captableURL = data;
						// If backend returned an unsigned CloudFront URL, get a signed one
						if (captableURL.includes('d3joenepwjic7e.cloudfront.net/')) {
							const docName = captableURL.split('/').pop();
							captableURL = await window.getSnapshotUrl(docName);
						}
						const response = await fetch(captableURL);
						if (!response.ok) {
							failure = 'cap table URL returned ' + response.status;
						} else {
							const parsed = JSON.parse(await response.text());
							if (Array.isArray(parsed?.data)) {
								console.log('### Got URL ShareholderData (from ' + captableURL + '): ' + (Date.now() - AppState.refreshTime) + ' ms');
								capTableRes = parsed;
							} else failure = 'cap table file not in expected format';
						}
					} else {
						failure = 'unexpected shareholderData response: ' + String(JSON.stringify(data)).slice(0, 200);
					}
				} catch (error) {
					failure = 'shareholderData request failed: ' + (error?.message || error);
				}

				// Outside the try so a rendering error isn't mistaken for a load failure
				if (capTableRes) {
					applyCapTable(capTableRes, freshHash, reusedSaved ? 'Saved cap table confirmed current, no download' : 'Cap table rebuilt', true);
					return;
				}
				if (attempt < self._MAX_REFRESH_ATTEMPTS) {
					console.warn('[refreshCapTable] attempt ' + attempt + ' failed (' + failure + '), retrying in ' + self._REFRESH_RETRY_DELAY_MS + 'ms');
					await new Promise((resolve) => setTimeout(resolve, self._REFRESH_RETRY_DELAY_MS));
					return generateCapTableLegacy(attempt + 1);
				}
				capTableFailed('refreshCapTable: ' + failure, { page: window.location.pathname, attempt });
			}

			function generateCapTable(res) {
				// #6271: filter to effective status scope for isSUPV only -- Executives see the
				// full cap table by design, matching the isSupv && !isExec path in index.js.
				if (isSupv && !isExec && !isAlera) {
					const scopeStatuses = (
						userData.clients[client].override_statuses?.length ? userData.clients[client].override_statuses : [userData.clients[client].status]
					).map((s) =>
						String(s || '')
							.toLowerCase()
							.trim()
					);

					res = {
						data: res.data.filter((user) => scopeStatuses.includes(user.Item?.clients[client].status?.toLowerCase().trim())),
					};
				}

				// A saved Alera cap table already has its units: no CSV pass
				if (isAlera && !res.saved) self._callbacks.getAleraHoldingsFromFile('capTable', res, false, false, false, '');
				else {
					console.log('CapTable BEFORE complete: ' + (Date.now() - AppState.refreshTime) + ' ms');
					capTableData = [];
					res.data.forEach(function (elem) {
						// not sure this is the behavior we want for a brand new client with little or no data
						if (!elem['Item'] || !elem['Item']['clients']) return;

						// AUTOPOPULATE any userlisted user that is missing client data
						let thisUserData = elem['Item'];
						let userClient = thisUserData.clients[client];
						if (!userClient || !userClient['shares']) {
							thisUserData = self._callbacks.autoPopulateUser(thisUserData.userid, thisUserData);
							elem['Item'] = thisUserData;
						}

						// only push populated users
						if (!elem['Item'].clients[client].shares) {
							console.log('Bad client data:');
							console.log(elem);
						} else {
							capTableData.push(elem['Item']);
						}
					});
					capTableData = removeNthAdmins(capTableData);
					if (blurNames) {
						let blurNamesArray = blurNames.split('\r\n');
						for (let i = 0; i < capTableData.length; i++) capTableData[i].user_name = blurNamesArray[i];
					}
					NthUtil.sessionStore.updateCapTable(client, capTableData);
					console.log('CapTable AFTER complete: ' + (Date.now() - AppState.refreshTime) + ' ms');
					if (isAlera) document.dispatchEvent(new Event('ExtDataComplete'));
					document.dispatchEvent(new Event('CapTableReady'));
					self._callbacks.sessionStorageSize();
					let goToAdmin = document.getElementById('goToAdmin');
					if (goToAdmin) {
						goToAdmin.style.pointerEvents = null;
						goToAdmin.style.backgroundColor = null;
						goToAdmin.innerHTML = AppState.pn.includes('admin') ? 'View Investor Portal' : 'Go to Admin';
					}
				}
			}
		}
	},
};

export { SessionStorageService };
